/**
 * FIM read hooks — typed wrappers over the FIM service and Rolodex-backed
 * endpoints. Source lists and records are served by fim-service so the
 * configured Postgres/Rolodex storage flag is applied centrally.
 *
 * `useGetRolodexEntriesByLoadOptions` accepts arbitrary string entry `type`s
 * (unlike the enum-typed `useRolodexEntries`), which is what we need for the
 * `fim-*` family that isn't in the `RolodexEntryType` enum.
 */

import { useRuntimeVariables } from 'app-kit'
import { isAxiosError } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { useFacilityModelAccess } from 'features/facilityModel/access'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import useSWR, { preload } from 'swr'
import type { RolodexLoadedEntryJson } from 'types'

import { selectFusionRuns } from './fusionRuns'
import { beginInteractiveRead, endInteractiveRead } from './interactiveReads'
import {
  type ConstraintKind,
  FIM_ENTRY_CONSTRAINT,
  FIM_ENTRY_FUSION_RUN,
  FIM_ENTRY_REVIEW,
  FIM_ENTRY_SOURCE_LIST,
  FIM_ENTRY_SOURCE_RECORD,
  type FimAliasRow,
  type FimDatapointSubjectLink,
  type FimEntry,
  type FimFusionRunEntry,
  type FimSourceListEntry,
  type FimSourceRecordEntry,
  type FimSubjectData,
  type FimSubjectMatch,
  type FimSubjectRecord,
  type FimSubjectSummary,
  type FimSubjectSummaryData,
  type FusionRunData,
  type MatchRunFullDiagnostics,
  type MatchRunView,
  type ReviewData,
} from './types'
import type { SourceListSummary } from './useFimService'

const FUSION_RUN_POLL_MS = 5000

/**
 * Build the `{ site, organization }` tag filter Rolodex expects. Tags are only
 * included when defined so an unselected site doesn't over-constrain the query.
 */
function siteTags(
  siteID?: string,
  organizationID?: string
): Record<string, string[]> {
  // Site is the deterministic partition (architecture §1); fim-service dropped
  // organization tenancy ("site a required path param; drop organization"), so
  // current fim-* entries carry NO organization tag. Filtering on it would
  // silently exclude every current run/subject and resurrect stale org-tagged
  // ones — scope by site only.
  void organizationID
  const tags: Record<string, string[]> = {}
  if (siteID) tags.site = [siteID]
  return tags
}

/** Flatten `{ entry, links? }[]` → `FimEntry[]` (id/type/tags/data). */
export function unwrap<TData>(
  loaded: RolodexLoadedEntryJson[] | undefined
): FimEntry<TData>[] {
  if (!loaded) return []
  return loaded.map(({ entry }) => ({
    id: entry.id,
    type: entry.type as unknown as string,
    tags: (entry.tags ?? {}) as Record<string, string>,
    data: (entry.data ?? {}) as TData,
  }))
}

interface FimReadOptions {
  siteID?: string
  organizationID?: string
  /** Skip the query entirely (e.g. before a site is selected). */
  enabled?: boolean
}

/** `fim-source-list` entries for a site — the creation runs. */
export function useFimSourceLists(opts: FimReadOptions = {}) {
  const { siteID, enabled = true } = opts
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const shouldFetch = enabled && isAuthenticated && !!siteID
  const { data, ...rest } = useSWR<SourceListSummary[], Error>(
    shouldFetch ? ['fim-source-lists', FIM_SERVICE_URL, siteID] : null,
    async () => {
      const url = `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteID as string)}/sources`
      return authorizedAxios
        .get<SourceListSummary[]>(url)
        .then((response) => response.data)
    }
  )
  const sourceLists = useMemo(
    () =>
      (data ?? []).map(
        (summary): FimSourceListEntry => ({
          id: summary.id,
          type: FIM_ENTRY_SOURCE_LIST,
          tags: { site: summary.site },
          data: {
            name: summary.name,
            adapter: summary.adapter,
            params: summary.params ?? {},
            created_at: summary.created_at ?? '',
            updated_at: summary.updated_at ?? null,
            record_count: summary.record_count,
          },
        })
      ),
    [data]
  )
  return { sourceLists, ...rest }
}

interface SourceRecordsPage {
  total: number
  records: { id: string; data: FimSourceRecordEntry['data'] }[]
}

/**
 * A source list's records, via the fim-service records endpoint. Blob-backed
 * lists (records stored as one JSONL file on the list entry) have no
 * per-record Rolodex entries, so the old Rolodex tag query returns nothing for
 * them — the endpoint reads whichever storage the list uses.
 */
export function useFimSourceRecords(
  opts: FimReadOptions & { sourceListIds?: string[] } = {}
) {
  const { siteID, organizationID, sourceListIds, enabled = true } = opts
  void organizationID
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const listIds = sourceListIds ?? []
  const shouldFetch =
    enabled && isAuthenticated && !!siteID && listIds.length > 0
  const { data, ...rest } = useSWR<SourceRecordsPage['records'], Error>(
    shouldFetch
      ? ['fim-source-records', FIM_SERVICE_URL, siteID, ...listIds]
      : null,
    async () => {
      const base = `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteID as string)}/sources`
      const pages = await Promise.all(
        listIds.map((listId) =>
          authorizedAxios
            .get<SourceRecordsPage>(
              `${base}/${encodeURIComponent(listId)}/records`,
              { params: { limit: 5000 } }
            )
            .then((r) => r.data.records)
        )
      )
      return pages.flat()
    }
  )
  const sourceRecords = useMemo<FimEntry<FimSourceRecordEntry['data']>[]>(
    () =>
      (data ?? []).map((r) => ({
        id: r.id,
        type: FIM_ENTRY_SOURCE_RECORD,
        tags: {},
        data: r.data,
      })),
    [data]
  )
  return { sourceRecords, ...rest }
}

/** `GET /sources/records/by-ids` — the resolved records plus the ids that
 * genuinely do not exist. More than 500 ids per request is a 400. */
interface SourceRecordsByIds {
  records: { id: string; data: FimSourceRecordEntry['data'] }[]
  missing: string[]
}

const RECORDS_BY_IDS_LIMIT = 500

/**
 * Source records fetched by explicit ids — the review backlink "click a
 * constituent → show its source record" drill-in.
 *
 * Served by fim-service, not Rolodex, for the reason `useFimSourceRecords`
 * documents: a blob-backed list keeps its records as one JSONL file on the
 * list entry and creates NO per-record Rolodex entry, so a Rolodex load by id
 * finds nothing for them. `missing` is the endpoint's own verdict on which ids
 * are absent — never infer it from a length comparison. Ids past the
 * endpoint's per-request cap are chunked.
 *
 * `runId` narrows the server-side scan to that run's source lists. Without it
 * the endpoint scans every list on the site, cheapest-first, which can mean
 * downloading a 200k-record blob that holds none of the wanted ids. Callers
 * that know the projection's run (`subject.data.fusion_run_id`) should pass it.
 */
export function useFimSourceRecordsByIds(
  siteID: string | undefined,
  ids: string[] | undefined,
  runId?: string | null
) {
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const wanted = useMemo(() => Array.from(new Set(ids ?? [])), [ids])
  const shouldFetch = isAuthenticated && !!siteID && wanted.length > 0
  const { data, ...rest } = useSWR<SourceRecordsByIds, Error>(
    shouldFetch
      ? [
          'fim-source-records-by-ids',
          FIM_SERVICE_URL,
          siteID,
          runId ?? '',
          ...wanted,
        ]
      : null,
    async () => {
      const url = `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteID as string)}/sources/records/by-ids`
      const chunks: string[][] = []
      for (let i = 0; i < wanted.length; i += RECORDS_BY_IDS_LIMIT) {
        chunks.push(wanted.slice(i, i + RECORDS_BY_IDS_LIMIT))
      }
      const pages = await Promise.all(
        chunks.map((chunk) =>
          authorizedAxios
            .get<SourceRecordsByIds>(url, {
              params: {
                ids: chunk.join(','),
                ...(runId ? { run_id: runId } : {}),
              },
            })
            .then((r) => r.data)
        )
      )
      return {
        records: pages.flatMap((page) => page.records ?? []),
        missing: pages.flatMap((page) => page.missing ?? []),
      }
    }
  )
  const records = useMemo<FimSourceRecordEntry[]>(
    () =>
      (data?.records ?? []).map((record) => ({
        id: record.id,
        type: FIM_ENTRY_SOURCE_RECORD,
        tags: {},
        data: record.data,
      })),
    [data]
  )
  const byId = useMemo(() => {
    const m = new Map<string, FimSourceRecordEntry>()
    for (const r of records) m.set(r.id, r)
    return m
  }, [records])
  return { records, byId, missing: data?.missing ?? [], ...rest }
}

/** One page from a cursor-paginated fim-service collection endpoint. */
interface CursorPage<TItem> {
  next_cursor: string | null
  items: TItem[]
}

/**
 * Drain a cursor-paginated fim-service collection into one array. The reads
 * are keyset-paginated server-side, so the loop replaces the old bounded
 * Rolodex loads (and their silent truncation ceilings) with a complete read.
 */
async function fetchAllPages<TItem>(
  get: (params: Record<string, string | number | boolean>) => Promise<{
    data: Record<string, unknown>
  }>,
  itemsKey: string,
  params: Record<string, string | number | boolean>,
  pageSize: number
): Promise<TItem[]> {
  const items: TItem[] = []
  let cursor: string | null = null
  do {
    const page: CursorPage<TItem> = await get({
      ...params,
      limit: pageSize,
      ...(cursor ? { cursor } : {}),
    }).then((r) => ({
      next_cursor: (r.data.next_cursor as string | null) ?? null,
      items: (r.data[itemsKey] as TItem[]) ?? [],
    }))
    items.push(...page.items)
    cursor = page.next_cursor
  } while (cursor)
  return items
}

const SUBJECTS_PAGE_SIZE = 5000
const ALIASES_PAGE_SIZE = 20000

/** A Subject as it arrives on the wire from the subjects endpoints. */
interface ApiSubjectRecord<TData> {
  subject_uid: string
  entry_id: string
  status: 'review' | 'proposed'
  datapoints?: string[]
  /** Search (q) offset-mode responses only; null on plain reads and absent
   * on cursor-mode drains. */
  match?: FimSubjectMatch | null
  data: TData
}

const toSubjectRecord = <TData>(r: ApiSubjectRecord<TData>) => ({
  id: r.entry_id,
  subjectUid: r.subject_uid,
  status: r.status,
  datapoints: r.datapoints ?? [],
  ...(r.match ? { match: r.match } : {}),
  data: r.data,
})

/**
 * Alias rows for a fusion run (the crosswalk successor), from the fim-service
 * aliases endpoint. Defaults to the routing keys (`identity_only`) and to the
 * published run; run-gated exactly like `useFimSubjects`.
 */
export function useFimAliases(
  opts: FimReadOptions & {
    runId?: string | null
    identityOnly?: boolean
    /** Point-read scope: only these subjects' rows (bare uids, server caps at
     * 50 repeated params). Omit for the whole-run read. */
    subjectUids?: string[]
  } = {}
) {
  const {
    siteID,
    enabled = true,
    runId,
    identityOnly = true,
    subjectUids,
  } = opts
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { publishedRun } = useFimFusionRuns({ siteID, enabled })
  const selectedRunId = runId ?? publishedRun?.id ?? null
  const shouldFetch =
    enabled &&
    isAuthenticated &&
    !!siteID &&
    !!selectedRunId &&
    (subjectUids === undefined || subjectUids.length > 0)
  const { data, ...rest } = useSWR<FimAliasRow[], Error>(
    shouldFetch
      ? [
          'fim-aliases',
          FIM_SERVICE_URL,
          siteID,
          selectedRunId,
          identityOnly,
          ...(subjectUids ?? []),
        ]
      : null,
    () =>
      fetchAllPages<FimAliasRow>(
        (params) => {
          const search = new URLSearchParams()
          for (const [key, value] of Object.entries(params)) {
            search.set(key, String(value))
          }
          // Repeated subject_uid params — the server ANDs them with the run
          // scope and returns only those subjects' rows.
          for (const uid of subjectUids ?? []) {
            search.append('subject_uid', uid)
          }
          return authorizedAxios.get(
            `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteID as string)}/aliases?${search.toString()}`
          )
        },
        'aliases',
        {
          run_id: selectedRunId as string,
          identity_only: identityOnly,
        },
        ALIASES_PAGE_SIZE
      )
  )
  return { aliasRows: data ?? [], publishedRun, selectedRunId, ...rest }
}

/** A normalized alias↔alias merge edge for the crosswalk graph view. */
export interface AliasEdge {
  a: string
  b: string
  provenance: 'human' | 'agent'
  kind?: ConstraintKind
  confidence?: number
  rationale?: string | null
}

/** An OCR/extraction correction: the wrong tag → its corrected form. */
export interface OcrCorrection {
  wrong: string
  corrected: string
  rationale?: string | null
}

/** Supersede lineage: a prior subject uid redirected into its survivor. */
export interface SupersedeRecord {
  from_id: string
  into_id: string
  run?: string | null
  /** Absent on entries written before the field existed. */
  created_at?: string | null
}

/**
 * `fim-constraint` entries — the durable alias↔alias edge layer. Holds BOTH
 * human constraints (must_link / cannot_link) and agent-proposed edges (tagged
 * `provenance=agent`, carrying confidence + rationale). Partitioned by that tag
 * into a single normalized `AliasEdge[]` for the graph.
 */
export function useFimConstraints(
  opts: FimReadOptions & {
    /** Server-side provenance-tag scope (e.g. `supersede` for lineage-only
     * consumers) — the full constraint set is site-wide and OVERLOADED, so
     * surfaces that need one variant should not pull the rest. */
    provenance?: 'supersede' | 'agent' | 'ocr_variant'
  } = {}
) {
  const { siteID, organizationID, enabled = true, provenance } = opts
  const tags = siteTags(siteID, organizationID)
  if (provenance) tags.provenance = [provenance]
  const { data, ...rest } = useGetRolodexEntriesByLoadOptions<
    RolodexLoadedEntryJson[]
  >(
    enabled
      ? {
          type: [FIM_ENTRY_CONSTRAINT],
          tags,
          limit: 20000,
        }
      : undefined
  )
  const decoded = useMemo(() => {
    const rows = unwrap<Record<string, unknown>>(data)
    const edges: AliasEdge[] = []
    const corrections: OcrCorrection[] = []
    const lineage: SupersedeRecord[] = []
    for (const r of rows) {
      const prov = r.tags.provenance
      // The `fim-constraint` type is OVERLOADED — four provenance variants.
      // Decode each into its own shape (ocr_variant/supersede used to
      // mis-render as human edges / get dropped).
      if (prov === 'supersede') {
        const from = r.data.from_id as string | undefined
        const into = r.data.into_id as string | undefined
        if (from && into) {
          lineage.push({
            from_id: from,
            into_id: into,
            run: (r.data.run as string | null | undefined) ?? null,
            created_at:
              (r.data.created_at as string | null | undefined) ?? null,
          })
        }
        continue
      }
      const a = r.data.alias_a_norm as string | undefined
      const b = r.data.alias_b_norm as string | undefined
      if (!a || !b) continue
      if (prov === 'ocr_variant') {
        corrections.push({
          wrong: a,
          corrected: b,
          rationale: (r.data.rationale as string | null | undefined) ?? null,
        })
      } else if (prov === 'agent') {
        edges.push({
          a,
          b,
          provenance: 'agent',
          confidence:
            typeof r.data.confidence === 'number'
              ? r.data.confidence
              : undefined,
          rationale: (r.data.rationale as string | null | undefined) ?? null,
        })
      } else {
        edges.push({
          a,
          b,
          provenance: 'human',
          kind: r.data.kind as ConstraintKind | undefined,
        })
      }
    }
    return { edges, corrections, lineage }
  }, [data])
  return { ...decoded, ...rest }
}

/**
 * `fim-fusion-run` entries — run history (status + counts). Exposes two run
 * selectors because a fusion run is now async: `latestAttempt` is the newest
 * run of any status (drives kickoff / live-status UX), while `publishedRun` is
 * the newest `done` run (the coherent projection every subject/output/review
 * view must scope to — see `selectFusionRuns`).
 */
export function useFimFusionRuns(opts: FimReadOptions = {}) {
  const { siteID, organizationID, enabled = true } = opts
  const { data, ...rest } = useGetRolodexEntriesByLoadOptions<
    RolodexLoadedEntryJson[]
  >(
    enabled
      ? {
          type: [FIM_ENTRY_FUSION_RUN],
          tags: siteTags(siteID, organizationID),
          limit: 200,
        }
      : undefined
  )
  const fusionRuns = useMemo(
    () => unwrap<FimFusionRunEntry['data']>(data),
    [data]
  )
  const { latestAttempt, publishedRun } = useMemo(
    () => selectFusionRuns(fusionRuns),
    [fusionRuns]
  )
  return { fusionRuns, latestAttempt, publishedRun, ...rest }
}

/** Live status of a single fusion run, read off the `fim-fusion-run` entry. */
export interface FusionRunStatus {
  status?: string
  phase?: string | null
  error?: string | null
  started?: string
  ended?: string | null
  counts?: FusionRunData['counts']
}

/**
 * Poll ONE `fim-fusion-run` entry by id for live progress, sentinel-style:
 * while its status is `running` the entry is re-fetched every 5s, then polling
 * stops. Disabled (no request) until a `runId` is passed — i.e. right after a
 * kickoff. Deliberately keys off the EXACT run's status, never "some running
 * record in the list", so a stale in-flight record can't drive the poll.
 */
export function useFusionRunStatus(
  siteID: string | undefined,
  runId: string | undefined
): FusionRunStatus & { mutate: () => void } {
  const { data, mutate } = useGetRolodexEntriesByLoadOptions<
    RolodexLoadedEntryJson[]
  >(
    siteID && runId
      ? { ids: [runId], type: [FIM_ENTRY_FUSION_RUN], limit: 1 }
      : undefined,
    {
      refreshInterval: (latest) =>
        latest?.[0]?.entry?.data?.status === 'running' ? FUSION_RUN_POLL_MS : 0,
    }
  )
  const run = unwrap<FusionRunData>(data)[0]
  return {
    status: run?.data.status,
    phase: run?.data.phase,
    error: run?.data.error,
    started: run?.data.started,
    ended: run?.data.ended,
    counts: run?.data.counts,
    mutate: () => {
      void mutate()
    },
  }
}

/**
 * Time-series-linkage run history (`fde-fim-match-run` records, served by the
 * fim-service runs endpoint). Newest first. While any listed run is `running`
 * the list re-fetches every 5s, sentinel-style, then polling stops.
 */
export function useMatchingRuns(siteID?: string) {
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const shouldFetch = isAuthenticated && !!siteID
  const { data, ...rest } = useSWR<MatchRunView[], Error>(
    shouldFetch ? ['fim-matching-runs', FIM_SERVICE_URL, siteID] : null,
    async () => {
      const res = await authorizedAxios.get<MatchRunView[]>(
        `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteID as string)}/matching/runs`
      )
      return res.data
    },
    {
      refreshInterval: (latest) =>
        latest?.some((r) => r.status === 'running') ? FUSION_RUN_POLL_MS : 0,
    }
  )
  return { matchingRuns: data ?? [], ...rest }
}

/**
 * A linkage run's complete unmatched/ambiguous lists. Fetched once per run
 * when `enabled` first turns true (View-all click), never polled. A 404 means
 * the run predates full-list recording — surfaced as `notRecorded`, not an
 * error.
 */
export function useMatchRunFullDiagnostics(
  siteID: string | undefined,
  runId: string | undefined,
  enabled: boolean
) {
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const shouldFetch = enabled && isAuthenticated && !!siteID && !!runId
  const { data, error, isLoading } = useSWR<MatchRunFullDiagnostics | null>(
    shouldFetch
      ? ['fim-match-run-full-diagnostics', FIM_SERVICE_URL, siteID, runId]
      : null,
    async () => {
      try {
        const res = await authorizedAxios.get<MatchRunFullDiagnostics>(
          `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteID as string)}/matching/runs/${encodeURIComponent(runId as string)}/diagnostics/full`
        )
        return res.data
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) return null
        throw e
      }
    },
    { revalidateOnFocus: false }
  )
  return {
    full: data ?? undefined,
    notRecorded: data === null,
    isLoading: shouldFetch && isLoading,
    error,
  }
}

/**
 * CURSOR-MODE summary drain of a run's Subjects — for the few internal panels
 * that genuinely need every row at once (crosswalk join/graph, run diff).
 * Interactive tables use `useFimSubjectsPage` instead.
 *
 * Scoped to a single fusion run (default: the latest PUBLISHED run) so the view
 * reflects one coherent run and stale subjects from prior/narrower runs never
 * leak in. Scoping to the newest published run (not the newest attempt) keeps an
 * in-flight run — which has no subjects yet — from emptying the view. Pass an
 * explicit `runId` to pin a run (the run picker). Durable human/agent decisions
 * don't live here — they persist as fim-review / fim-constraint — so
 * latest-only is lossless.
 */
export function useFimSubjectsDrain(
  opts: FimReadOptions & {
    runId?: string | null
    /** Server-side kind scope, when a view only needs one kind. */
    kind?: string
    /** Server-side status scope (`review` | `proposed`). The review queue
     * wants exactly the `review` slice. */
    status?: string
  } = {}
) {
  const { siteID, enabled = true, runId, kind, status } = opts
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { fusionRuns, publishedRun } = useFimFusionRuns({ siteID, enabled })
  const selectedRunId = runId ?? publishedRun?.id ?? null
  // Scoping needs the run first; never fall back to loading the whole site
  // (that is exactly what leaks stale subjects). No run yet → no subjects.
  const shouldFetch = enabled && isAuthenticated && !!siteID && !!selectedRunId
  // The list drain is ALWAYS summary-shaped: full payloads over a whole run
  // blow Cloud Run's 32MB response cap mid-drain. Anything needing evidence
  // fields fetches the specific subject via the detail / by-ids reads.
  const { data, ...rest } = useSWR<FimSubjectSummary[], Error>(
    shouldFetch
      ? [
          'fim-subjects',
          FIM_SERVICE_URL,
          siteID,
          selectedRunId,
          kind ?? '',
          status ?? '',
        ]
      : null,
    async () => {
      const raw = await fetchAllPages<ApiSubjectRecord<FimSubjectSummaryData>>(
        (params) =>
          authorizedAxios.get(
            `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteID as string)}/subjects`,
            { params }
          ),
        'subjects',
        {
          run_id: selectedRunId as string,
          shape: 'summary',
          ...(kind ? { kind } : {}),
          ...(status ? { status } : {}),
        },
        SUBJECTS_PAGE_SIZE
      )
      return raw.map(toSubjectRecord)
    }
  )
  const assets = useMemo(() => data ?? [], [data])
  return { assets, fusionRuns, publishedRun, selectedRunId, ...rest }
}

/** Filters for the paged subjects read; all conditions are ANDed. */
export interface FimSubjectsPageFilters {
  kind?: string
  status?: 'review' | 'proposed'
  reviewCategory?: string
  validationStatus?: string
  type?: string
  subsystemId?: string
  /** review_reason IS NOT NULL OR status='review'. */
  needsReview?: boolean
  minSources?: number
  /** 0..1 fraction (the percent UI divides by 100). */
  minConfidence?: number
  /** Explicit uid scope (server caps at 2000). */
  uids?: string[]
  /** Repeated search terms, each matched against name/type/alias haystack. */
  q?: string[]
}

export type FimSubjectsSortKey =
  | 'name'
  | 'kind'
  | 'type'
  | 'subsystem'
  | 'confidence'
  | 'validation'
  | 'status'

export const SUBJECTS_PAGE_LIMIT = 100

interface SubjectsPageResponse {
  run_id: string
  subjects: ApiSubjectRecord<FimSubjectSummaryData>[]
  next_cursor: string | null
  total: number | null
  /** Search responses cap `total` at 10,000; render capped totals as "N+". */
  total_capped?: boolean
}

const subjectsPageSearch = (
  runId: string,
  limit: number,
  offset: number,
  sort: FimSubjectsSortKey | undefined,
  order: 'asc' | 'desc',
  filters: FimSubjectsPageFilters
): URLSearchParams => {
  const search = new URLSearchParams()
  search.set('run_id', runId)
  search.set('shape', 'summary')
  search.set('limit', String(limit))
  search.set('offset', String(offset))
  // No sort → the server default applies: relevance-tiered under q,
  // name asc otherwise. Sending a sort would defeat relevance ordering.
  if (sort) {
    search.set('sort', sort)
    search.set('order', order)
  }
  if (filters.kind) search.set('kind', filters.kind)
  if (filters.status) search.set('status', filters.status)
  if (filters.reviewCategory) {
    search.set('review_category', filters.reviewCategory)
  }
  if (filters.validationStatus) {
    search.set('validation_status', filters.validationStatus)
  }
  if (filters.type) search.set('type', filters.type)
  if (filters.subsystemId) search.set('subsystem_id', filters.subsystemId)
  if (filters.needsReview) search.set('needs_review', 'true')
  if (filters.minSources !== undefined) {
    search.set('min_sources', String(filters.minSources))
  }
  if (filters.minConfidence !== undefined) {
    search.set('min_confidence', String(filters.minConfidence))
  }
  if (filters.uids?.length) search.set('uids', filters.uids.join(','))
  for (const term of filters.q ?? []) {
    if (term.trim()) search.append('q', term.trim())
  }
  return search
}

/**
 * OFFSET-MODE page of a run's Subjects — the server-driven table read.
 * Filtering, search, sort, and the filtered `total` all happen in fim-service;
 * the page carries summary-shaped rows only.
 */
export function useFimSubjectsPage(
  opts: FimReadOptions & {
    runId?: string | null
    limit?: number
    offset?: number
    sort?: FimSubjectsSortKey
    order?: 'asc' | 'desc'
    filters?: FimSubjectsPageFilters
  } = {}
) {
  const {
    siteID,
    enabled = true,
    runId,
    limit = SUBJECTS_PAGE_LIMIT,
    offset = 0,
    sort,
    order = 'asc',
    filters = {},
  } = opts
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { fusionRuns, publishedRun } = useFimFusionRuns({ siteID, enabled })
  const selectedRunId = runId ?? publishedRun?.id ?? null
  const shouldFetch = enabled && isAuthenticated && !!siteID && !!selectedRunId
  const search = selectedRunId
    ? subjectsPageSearch(selectedRunId, limit, offset, sort, order, filters)
    : undefined
  const fetchPage = useCallback(
    async (params: string) => {
      beginInteractiveRead()
      try {
        // A page/search must answer or fail visibly — an unbounded hang
        // leaves the table silently stale (observed: q= searches wedging
        // server-side).
        const res = await authorizedAxios.get<SubjectsPageResponse>(
          `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteID as string)}/subjects?${params}`,
          { timeout: 30_000 }
        )
        return {
          rows: res.data.subjects.map(toSubjectRecord),
          total: res.data.total,
          totalCapped: res.data.total_capped ?? false,
        }
      } finally {
        endInteractiveRead()
      }
    },
    [FIM_SERVICE_URL, authorizedAxios, siteID]
  )
  const { data, ...rest } = useSWR<
    { rows: FimSubjectSummary[]; total: number | null; totalCapped: boolean },
    Error
  >(
    shouldFetch && search
      ? ['fim-subjects-page', FIM_SERVICE_URL, siteID, search.toString()]
      : null,
    () => fetchPage(search?.toString() ?? ''),
    { keepPreviousData: true }
  )

  // Prefetch the NEXT page on idle once this one lands, so the first pager
  // click is instant. One shot per key; SWR's cache serves the click.
  const prefetched = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!data || !shouldFetch || !selectedRunId) return
    if (data.total === null || offset + limit >= data.total) return
    const next = subjectsPageSearch(
      selectedRunId,
      limit,
      offset + limit,
      sort,
      order,
      filters
    ).toString()
    if (prefetched.current.has(next)) return
    prefetched.current.add(next)
    const idle: (cb: () => void) => void =
      typeof window.requestIdleCallback === 'function'
        ? (cb) => window.requestIdleCallback(cb)
        : (cb) => void window.setTimeout(cb, 250)
    idle(() => {
      void preload(['fim-subjects-page', FIM_SERVICE_URL, siteID, next], () =>
        fetchPage(next)
      )
    })
  }, [
    data,
    shouldFetch,
    selectedRunId,
    offset,
    limit,
    sort,
    order,
    filters,
    FIM_SERVICE_URL,
    siteID,
    fetchPage,
  ])

  return {
    rows: data?.rows ?? [],
    total: data?.total ?? null,
    totalCapped: data?.totalCapped ?? false,
    fusionRuns,
    publishedRun,
    selectedRunId,
    ...rest,
  }
}

/** Header counts + facet options for a run's Subjects, from /subjects/stats. */
export interface FimSubjectStats {
  run_id: string
  total: number
  by_kind: Record<string, number>
  needs_review: number
  validated_by_kind: Record<string, number>
  types: string[]
  subsystems: string[]
}

export function useFimSubjectStats(
  opts: FimReadOptions & { runId?: string | null } = {}
) {
  const { siteID, enabled = true, runId } = opts
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { publishedRun } = useFimFusionRuns({ siteID, enabled })
  const selectedRunId = runId ?? publishedRun?.id ?? null
  const shouldFetch = enabled && isAuthenticated && !!siteID && !!selectedRunId
  const { data, ...rest } = useSWR<FimSubjectStats, Error>(
    shouldFetch
      ? ['fim-subject-stats', FIM_SERVICE_URL, siteID, selectedRunId]
      : null,
    async () => {
      beginInteractiveRead()
      try {
        const res = await authorizedAxios.get<FimSubjectStats>(
          `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteID as string)}/subjects/stats`,
          { params: { run_id: selectedRunId as string } }
        )
        return res.data
      } finally {
        endInteractiveRead()
      }
    }
  )
  return { stats: data, publishedRun, selectedRunId, ...rest }
}

/**
 * One Subject by uid from the published run — the deep-link path for a
 * Subject the bounded catalog join missed. A 404 (uid not in the run)
 * resolves to `undefined` rather than an error.
 */
export function useFimSubject(
  siteID: string | undefined,
  subjectUid: string | undefined,
  opts: { enabled?: boolean } = {}
) {
  const { enabled = true } = opts
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { publishedRun } = useFimFusionRuns({ siteID, enabled })
  const runId = publishedRun?.id ?? null
  const shouldFetch =
    enabled && isAuthenticated && !!siteID && !!subjectUid && !!runId
  const { data, ...rest } = useSWR<FimSubjectRecord | null, Error>(
    shouldFetch
      ? ['fim-subject', FIM_SERVICE_URL, siteID, runId, subjectUid]
      : null,
    async () => {
      try {
        const res = await authorizedAxios.get<ApiSubjectRecord<FimSubjectData>>(
          `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteID as string)}/subjects/${encodeURIComponent(subjectUid as string)}`,
          { params: { run_id: runId as string } }
        )
        return toSubjectRecord(res.data)
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) return null
        throw e
      }
    }
  )
  return { subject: data ?? undefined, notFound: data === null, ...rest }
}

/** Uids per by-ids request — keeps the GET query string well under URL caps. */
const BY_IDS_CHUNK = 100

function useSubjectsByIdsShaped<TData>(
  siteID: string | undefined,
  subjectUids: string[] | undefined,
  shape: 'full' | 'summary'
) {
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { publishedRun } = useFimFusionRuns({ siteID })
  const runId = publishedRun?.id ?? null
  const uids = subjectUids ?? []
  const shouldFetch = isAuthenticated && !!siteID && !!runId && uids.length > 0
  const { data, ...rest } = useSWR<
    ReturnType<typeof toSubjectRecord<TData>>[],
    Error
  >(
    shouldFetch
      ? ['fim-subjects-by-ids', FIM_SERVICE_URL, siteID, runId, shape, ...uids]
      : null,
    async () => {
      const chunks: string[][] = []
      for (let i = 0; i < uids.length; i += BY_IDS_CHUNK) {
        chunks.push(uids.slice(i, i + BY_IDS_CHUNK))
      }
      const pages = await Promise.all(
        chunks.map((chunk) =>
          authorizedAxios
            .get<{ subjects: ApiSubjectRecord<TData>[] }>(
              `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteID as string)}/subjects/by-ids`,
              {
                params: {
                  run_id: runId as string,
                  uids: chunk.join(','),
                  ...(shape === 'summary' ? { shape } : {}),
                },
              }
            )
            .then((res) => res.data.subjects)
        )
      )
      return pages.flat().map(toSubjectRecord)
    }
  )
  return { subjects: data ?? [], ...rest }
}

/**
 * FULL Subjects by explicit uids from the published run — the on-demand
 * evidence fetch (references/dossier/associations) for a selected subject.
 * Missing uids are simply absent.
 */
export function useFimSubjectsByIds(
  siteID: string | undefined,
  subjectUids: string[] | undefined
) {
  return useSubjectsByIdsShaped<FimSubjectData>(siteID, subjectUids, 'full')
}

/**
 * Summary-shaped Subjects by explicit uids — for consumers that only need
 * identity/datapoints fields (e.g. instrument selection).
 */
export function useFimSubjectSummariesByIds(
  siteID: string | undefined,
  subjectUids: string[] | undefined
) {
  return useSubjectsByIdsShaped<FimSubjectSummaryData>(
    siteID,
    subjectUids,
    'summary'
  )
}

/** Response shape of `GET /subjects/by-datapoints`. */
interface SubjectsByDatapointsResponse {
  run_id: string | null
  datapoints: FimDatapointSubjectLink[]
  subjects: ApiSubjectRecord<FimSubjectSummaryData>[]
  missing: string[]
}

/**
 * Subject links for a set of pen datapoint ids, from the fim-service
 * `/subjects/by-datapoints` endpoint — the join a time-series pen row uses to
 * find its linked Subject(s). `canView` (Facility Model access) is the single
 * gate: it already covers both the permission tier and the Longridge release
 * gate, so no separate check is needed here. A 404 (the endpoint predates
 * deployment on this environment) degrades to the empty shape rather than
 * throwing, so pen rows simply render no icons until the companion
 * fim-service endpoint ships. No chunking: callers are capped by `MAX_PENS`.
 */
export function useFimSubjectsByDatapoints(
  siteID: string | undefined,
  datapointIds: string[] | undefined,
  opts: { enabled?: boolean; runId?: string | null } = {}
): {
  byDatapoint: Map<string, string[]>
  subjectsByUid: Map<string, FimSubjectSummary>
  missing: string[]
  runId: string | null
  isLoading: boolean
  error?: Error
} {
  const { enabled = true, runId } = opts
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { canView } = useFacilityModelAccess(siteID)
  const { publishedRun } = useFimFusionRuns({ siteID, enabled })
  const selectedRunId = runId ?? publishedRun?.id ?? null
  const wanted = useMemo(
    () => Array.from(new Set(datapointIds ?? [])),
    [datapointIds]
  )
  const shouldFetch =
    enabled &&
    isAuthenticated &&
    canView &&
    !!siteID &&
    !!selectedRunId &&
    wanted.length > 0
  const { data, error, isLoading } = useSWR<
    SubjectsByDatapointsResponse,
    Error
  >(
    shouldFetch
      ? [
          'fim-subjects-by-datapoints',
          FIM_SERVICE_URL,
          siteID,
          selectedRunId,
          ...wanted,
        ]
      : null,
    async () => {
      const search = new URLSearchParams()
      search.set('run_id', selectedRunId as string)
      search.set('shape', 'summary')
      for (const id of wanted) search.append('datapoint_id', id)
      try {
        const res = await authorizedAxios.get<SubjectsByDatapointsResponse>(
          `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteID as string)}/subjects/by-datapoints?${search.toString()}`
        )
        return res.data
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) {
          return { run_id: null, datapoints: [], subjects: [], missing: wanted }
        }
        throw e
      }
    }
  )
  const byDatapoint = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const link of data?.datapoints ?? []) {
      m.set(link.datapoint_id, link.subject_uids)
    }
    return m
  }, [data])
  const subjectsByUid = useMemo(() => {
    const m = new Map<string, FimSubjectSummary>()
    for (const subject of data?.subjects ?? []) {
      const record = toSubjectRecord(subject)
      m.set(record.subjectUid, record)
    }
    return m
  }, [data])
  return {
    byDatapoint,
    subjectsByUid,
    missing: data?.missing ?? [],
    runId: selectedRunId,
    isLoading: shouldFetch && isLoading,
    error,
  }
}

/**
 * `fim-review` entries — the durable, append-only human-decision audit. Written
 * by `fim-service` and (until now) never read back; this is the read side that
 * powers the decision-history / "trace previous decisions" surfaces.
 */
export function useFimReviews(opts: FimReadOptions = {}) {
  const { siteID, organizationID, enabled = true } = opts
  const { data, ...rest } = useGetRolodexEntriesByLoadOptions<
    RolodexLoadedEntryJson[]
  >(
    enabled
      ? {
          type: [FIM_ENTRY_REVIEW],
          tags: siteTags(siteID, organizationID),
          limit: 5000,
        }
      : undefined
  )
  const reviews = useMemo(() => unwrap<ReviewData>(data), [data])
  return { reviews, ...rest }
}
